import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  CircularProgress
} from '@mui/material';
import { CheckCircle, Celebration } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';

const SubscriptionSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshSubscription, user } = useAuth();
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    // Refresh subscription status after successful payment
    const verifySubscription = async () => {
      try {
        await refreshSubscription();
      } catch (err) {
        console.error('Error refreshing subscription:', err);
      } finally {
        setLoading(false);
      }
    };
    
    // Small delay to allow Stripe webhook to process
    const timer = setTimeout(verifySubscription, 2000);
    
    return () => clearTimeout(timer);
  }, [refreshSubscription]);
  
  const handleGoToDashboard = () => {
    navigate('/dashboard');
  };
  
  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          <CircularProgress size={48} sx={{ mb: 3 }} />
          <Typography variant="h5" gutterBottom>
            Confirming your subscription...
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Please wait while we activate your account.
          </Typography>
        </Paper>
      </Container>
    );
  }
  
  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Paper 
        elevation={3} 
        sx={{ 
          p: 4, 
          textAlign: 'center',
          background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)',
          color: 'white'
        }}
      >
        <Celebration sx={{ fontSize: 64, mb: 2 }} />
        
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
          Welcome to SalonLogic!
        </Typography>
        
        <Box sx={{ 
          bgcolor: 'rgba(255,255,255,0.2)', 
          borderRadius: 2, 
          p: 2, 
          my: 3,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 1
        }}>
          <CheckCircle />
          <Typography variant="h6">
            Your subscription is now active
          </Typography>
        </Box>
        
        <Typography variant="body1" sx={{ mb: 4, opacity: 0.9 }}>
          You now have full access to the SalonLogic Pricing Calculator.
          Start setting profitable prices for your salon services!
        </Typography>
        
        <Box sx={{ 
          bgcolor: 'white', 
          borderRadius: 2, 
          p: 3, 
          mb: 3,
          color: 'text.primary'
        }}>
          <Typography variant="h6" gutterBottom color="text.primary">
            What's included:
          </Typography>
          <Box sx={{ textAlign: 'left' }}>
            {[
              'Cost-based pricing calculations',
              'Service & product management',
              'Staff & overhead tracking',
              'PDF & Excel exports',
              'Professional price lists',
              'All future updates'
            ].map((feature, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <CheckCircle color="success" sx={{ fontSize: 20, mr: 1 }} />
                <Typography variant="body2">{feature}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
        
        <Button
          variant="contained"
          size="large"
          onClick={handleGoToDashboard}
          sx={{ 
            bgcolor: 'white', 
            color: 'success.main',
            px: 4,
            py: 1.5,
            fontSize: '1.1rem',
            '&:hover': {
              bgcolor: 'rgba(255,255,255,0.9)'
            }
          }}
        >
          Go to Dashboard →
        </Button>
        
        <Typography variant="body2" sx={{ mt: 3, opacity: 0.8 }}>
          Need help getting started?{' '}
          <a href="mailto:andrew@salonlogic.co.uk" style={{ color: 'white' }}>
            Contact Andrew
          </a>
        </Typography>
      </Paper>
    </Container>
  );
};

export default SubscriptionSuccess;